// IHGIND STUDIO - Artistic Fabrication Studio Data
// Repositioned from metal fabricator to artistic design collaborator

const MEDIA = process.env.REACT_APP_MEDIA_URL || 'https://files.ihgbrands.com/ihg-ind';
const getAIImage = (name) => `${MEDIA}/generated/${name}.webp`;

export const siteConfig = {
  name: "IHGIND Studio",
  tagline: "Artistic Metal Fabrication",
  subline: "Where architecture meets artistry. Collaborating with visionary architects and designers to bring expressive metal works to life.",
  motto: "Craft. Collaborate. Create.",
  phone: "+971 4 258 5754",
  mobile: "+971 55 991 0711",
  email: "studio@ihgind.com",
  address: "Jebel Ali Industrial Area 1, Dubai, UAE",
  whatsapp: "971559910711",
  parentCompany: "IHG Brands",
  parentUrl: "https://ihgbrands.com",
  website: "https://ihgind.com",
};

// New Studio-focused navigation
export const navLinks = [
  { label: "Studio", href: "/about" },
  { 
    label: "Studio Works", 
    href: "/studio-works",
    children: [
      { label: "Architectural Installations", href: "/studio-works/architectural" },
      { label: "Interior Expressions", href: "/studio-works/interiors" },
      { label: "Art Atelier", href: "/art-atelier" },
      { label: "Retail & Brand", href: "/studio-works/retail" },
    ]
  },
  { 
    label: "Collections", 
    href: "/collections",
    children: [
      { label: "The Screen Collection", href: "/collections/screens" },
      { label: "The Light Series", href: "/collections/light" },
      { label: "The Sculpt Series", href: "/collections/sculpt" },
    ]
  },
  { label: "Material Library", href: "/finishes" },
  { label: "Journal", href: "/insights" },
  { label: "Collaborate", href: "/collaborate" },
];

// Studio Works - The four pillars of the artistic studio
export const studioWorks = [
  {
    id: "architectural",
    slug: "architectural",
    title: "Architectural Installations",
    subtitle: "Facades • Screens • Cladding Systems",
    heroText: "Large-scale metal expressions that define buildings",
    description: "We partner with architects to create landmark facade systems, parametric screens, and architectural metal installations that become the defining character of built environments.",
    philosophy: "Every building tells a story. Our architectural installations become the visual language through which that story is expressed - through shadow, light, texture, and material.",
    get image() { return getAIImage('studio_architectural'); },
    projectCount: 120,
    expertise: ["Parametric Facades", "Perforated Systems", "Contemporary Mashrabiya", "Metal Cladding", "Feature Exteriors"],
    materials: ["Aluminum", "Stainless Steel", "Copper", "Bronze", "Corten"],
    approach: [
      { step: "Concept Dialogue", description: "Understanding the architectural vision and design intent" },
      { step: "Material Exploration", description: "Selecting materials that express the concept" },
      { step: "Engineering Integration", description: "Ensuring structural and environmental compliance" },
      { step: "Craft & Fabrication", description: "Precision manufacturing with artisan oversight" },
    ]
  },
  {
    id: "interiors",
    slug: "interiors",
    title: "Interior Expressions",
    subtitle: "Feature Walls • Sculptural Elements • Ceiling Systems",
    heroText: "Transforming interior spaces through metal artistry",
    description: "From sculptural staircases to backlit feature walls, we create interior metal elements that elevate spaces beyond the ordinary into the realm of experiential design.",
    philosophy: "Interiors are intimate experiences. Our metal works create moments of wonder - the play of light through perforations, the warmth of bronze, the precision of a perfectly crafted handrail.",
    get image() { return getAIImage('studio_interiors'); },
    projectCount: 200,
    expertise: ["Feature Walls", "Sculptural Staircases", "Metal Ceilings", "Balustrades", "Integrated Lighting"],
    materials: ["Brass", "Bronze", "PVD Coated Steel", "Mirror Polish Stainless", "Patinated Copper"],
    approach: [
      { step: "Space Understanding", description: "Reading the architecture and its emotional intent" },
      { step: "Detail Development", description: "Designing joins, edges, and material transitions" },
      { step: "Finish Selection", description: "Choosing surfaces that respond to light and touch" },
      { step: "Installation Excellence", description: "Site-specific fitting with white-glove care" },
    ]
  },
  {
    id: "art-atelier",
    slug: "art-atelier",
    title: "Sculptures & Art Commissions",
    subtitle: "The IHGIND Art Atelier",
    heroText: "Collaborating with artists to realize monumental visions",
    description: "Our Art Atelier brings together master craftsmen and contemporary artists to create sculptures, installations, and public art that push the boundaries of what's possible in metal.",
    philosophy: "Art has the power to transform public spaces, to create landmarks, to inspire dialogue. We are the hands that help artists realize their most ambitious visions in metal.",
    get image() { return getAIImage('studio_art'); },
    projectCount: 35,
    expertise: ["Monumental Sculptures", "Kinetic Art", "Public Installations", "Artist Collaborations", "Gallery Works"],
    materials: ["Corten Steel", "Bronze", "Stainless Steel", "Mixed Media", "Weathered Finishes"],
    approach: [
      { step: "Artist Partnership", description: "Becoming an extension of the artist's studio" },
      { step: "Prototyping", description: "Scale models and material experiments" },
      { step: "Fabrication", description: "Translating artistic vision into engineered reality" },
      { step: "Installation", description: "Site-specific installation with artist supervision" },
    ]
  },
  {
    id: "retail",
    slug: "retail",
    title: "Retail & Brand Installations",
    subtitle: "Storefronts • VM Displays • Brand Environments",
    heroText: "Crafting the physical expression of brands",
    description: "We help luxury brands and retailers create memorable physical environments through custom metalwork that embodies brand values and creates lasting impressions.",
    philosophy: "In an age of digital saturation, physical brand experiences matter more than ever. Our metalwork creates tactile, visual brand stories that customers remember.",
    get image() { return getAIImage('studio_retail'); },
    projectCount: 150,
    expertise: ["Flagship Stores", "Visual Merchandising", "Brand Signage", "Pop-up Installations", "Exhibition Stands"],
    materials: ["PVD Gold", "Rose Gold", "Mirror Finishes", "Matte Black Steel", "Illuminated Elements"],
    approach: [
      { step: "Brand Immersion", description: "Understanding the brand DNA and design language" },
      { step: "Concept Design", description: "Creating metal solutions that embody the brand" },
      { step: "Rapid Prototyping", description: "Fast iteration for retail timelines" },
      { step: "Global Rollout", description: "Scalable production for multi-site deployment" },
    ]
  }
];

// Signature Studio Collections - Productized offerings
export const studioCollections = [
  {
    id: "screens",
    slug: "screens",
    title: "The Screen Collection",
    subtitle: "Architectural Screening Systems",
    description: "A curated series of parametric and geometric screen designs that balance privacy, light, and architectural expression.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    designs: [
      // Geometric & Parametric
      { name: "Tessellation", pattern: "Geometric interlocking hexagons", category: "Parametric", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Voronoi", pattern: "Organic cellular structure", category: "Parametric", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Penrose", pattern: "Aperiodic tiling patterns", category: "Geometric", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Fibonacci Spiral", pattern: "Golden ratio curves", category: "Parametric", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Fractal Grid", pattern: "Self-similar recursive patterns", category: "Parametric", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Islamic & Arabic Inspired
      { name: "Mashrabiya Classic", pattern: "Traditional turned wood aesthetic", category: "Arabic Heritage", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Mashrabiya Modern", pattern: "Contemporary geometric interpretation", category: "Arabic Heritage", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Arabesque Flow", pattern: "Flowing Islamic geometric art", category: "Arabic Heritage", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Zellige", pattern: "Moroccan tile-inspired geometry", category: "Arabic Heritage", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Muqarnas", pattern: "3D honeycomb stalactite forms", category: "Arabic Heritage", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Organic & Nature-Inspired
      { name: "Leaf Canopy", pattern: "Overlapping botanical forms", category: "Organic", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Coral Reef", pattern: "Marine-inspired organic mesh", category: "Organic", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Wave Series", pattern: "Fluid ocean wave curves", category: "Organic", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Bamboo Forest", pattern: "Vertical organic rhythm", category: "Organic", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Desert Dune", pattern: "Wind-sculpted sand forms", category: "Organic", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Minimalist & Linear
      { name: "Grid Series", pattern: "Clean horizontal/vertical lines", category: "Minimalist", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Blade System", pattern: "Parallel angled louvers", category: "Minimalist", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Pixel Matrix", pattern: "Digital-inspired dot grid", category: "Minimalist", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Ribbon Flow", pattern: "Continuous woven metal strips", category: "Minimalist", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Horizon Lines", pattern: "Staggered horizontal bars", category: "Minimalist", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Contemporary & Avant-Garde
      { name: "Deconstructed", pattern: "Fragmented irregular forms", category: "Contemporary", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Kinetic Illusion", pattern: "Movement-suggesting geometry", category: "Contemporary", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Glitch Pattern", pattern: "Digital distortion aesthetic", category: "Contemporary", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Origami Fold", pattern: "Paper-fold inspired angular", category: "Contemporary", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Moiré Effect", pattern: "Layered interference patterns", category: "Contemporary", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" }
    ],
    applications: ["Building Facades", "Privacy Screens", "Car Park Cladding", "Shade Structures", "Interior Partitions", "Canopy Systems", "Balcony Screens", "Pool Enclosures"],
    materials: ["Aluminum", "Stainless Steel", "Corten Steel", "Brass", "Bronze", "Powder Coated Steel"],
    specifications: {
      maxPanelSize: "3000mm x 1500mm",
      openAreaRange: "15% - 60%",
      thicknessRange: "1.5mm - 6mm",
      finishOptions: "80+ standard finishes"
    }
  },
  {
    id: "light",
    slug: "light",
    title: "The Light Series",
    subtitle: "Metal + Light Integration",
    description: "A collaboration with Ledworld exploring the intersection of metal and illumination. Sculptural lighting, backlit panels, and integrated LED systems.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    partnership: "In collaboration with Ledworld",
    designs: [
      // Backlit Systems
      { name: "Glow Panels", type: "Backlit perforated panels", category: "Backlit", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Aurora Wall", type: "Gradient backlit feature", category: "Backlit", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Nebula Screen", type: "Perforated starfield effect", category: "Backlit", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Halo Frame", type: "Edge-lit metal frames", category: "Backlit", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Linear Systems
      { name: "Linear Light", type: "Integrated LED channels", category: "Linear", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Blade Runner", type: "Angled light strips", category: "Linear", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Infinity Line", type: "Continuous light ribbons", category: "Linear", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Grid Glow", type: "Illuminated grid systems", category: "Linear", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Sculptural Lighting
      { name: "Sculptural Pendants", type: "Metal and light sculptures", category: "Sculptural", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Cascade Chandelier", type: "Flowing light installation", category: "Sculptural", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Orbit Fixture", type: "Circular suspended lights", category: "Sculptural", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Cluster Cloud", type: "Grouped pendant systems", category: "Sculptural", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Helix Spiral", type: "Twisted light forms", category: "Sculptural", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Facade Integration
      { name: "Facade Illumination", type: "Building-scale light integration", category: "Facade", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Pixel Facade", type: "LED dot matrix exterior", category: "Facade", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Ribbon Facade", type: "Flowing exterior light", category: "Facade", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Crown Light", type: "Rooftop accent lighting", category: "Facade", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Interactive
      { name: "Responsive Wall", type: "Motion-activated lighting", category: "Interactive", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Touch Panel", type: "Interactive light surface", category: "Interactive", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Sound Wave", type: "Audio-reactive installation", category: "Interactive", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" }
    ],
    applications: ["Hospitality Lobbies", "Feature Walls", "Ceiling Installations", "Exterior Facades", "Retail Environments", "Corporate Atriums", "Museum Spaces", "Event Venues"],
    materials: ["Aluminum", "Stainless Steel", "Brass", "Bronze", "Acrylic Diffusers"],
    specifications: {
      ledTypes: "RGBW, Tunable White, Single Color",
      controlSystems: "DMX, DALI, Bluetooth, WiFi",
      maxPanelSize: "2400mm x 1200mm",
      ipRating: "IP20-IP65 available"
    }
  },
  {
    id: "sculpt",
    slug: "sculpt",
    title: "The Sculpt Series",
    subtitle: "Functional Art Objects",
    description: "Limited edition functional sculptures that blur the line between furniture and art. Reception desks, planters, benches, and objets d'art.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    designs: [
      // Reception & Counters
      { name: "Monolith Desk", type: "Sculptural reception counters", category: "Reception", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Wave Counter", type: "Flowing reception desk", category: "Reception", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Facet Desk", type: "Angular geometric counter", category: "Reception", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Ribbon Reception", type: "Continuous curved form", category: "Reception", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Planters & Vessels
      { name: "Vessel Planters", type: "Artistic metal planters", category: "Planters", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Cone Garden", type: "Tapered planter series", category: "Planters", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Cube Grove", type: "Geometric planter boxes", category: "Planters", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Spiral Pot", type: "Twisted vessel forms", category: "Planters", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Crater Bowl", type: "Organic basin planters", category: "Planters", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Seating
      { name: "Fold Benches", type: "Origami-inspired seating", category: "Seating", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Loop Bench", type: "Continuous curved seat", category: "Seating", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Block Stool", type: "Solid geometric perch", category: "Seating", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Shell Chair", type: "Organic metal seating", category: "Seating", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Wave Lounger", type: "Sculptural outdoor seating", category: "Seating", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      
      // Tables & Surfaces
      { name: "Form Tables", type: "Sculptural side tables", category: "Tables", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Slab Console", type: "Cantilevered metal surface", category: "Tables", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Nest Tables", type: "Interlocking table set", category: "Tables", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Float Coffee", type: "Suspended tabletop", category: "Tables", trending: true },
      
      // Art Objects
      { name: "Totem Column", type: "Vertical art sculpture", category: "Art", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Sphere Series", type: "Polished metal spheres", category: "Art", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Kinetic Mobile", type: "Moving metal sculpture", category: "Art", trending: true, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" },
      { name: "Mirror Fragment", type: "Reflective wall art", category: "Art", trending: false, image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp" }
    ],
    applications: ["Corporate Lobbies", "Hotel Common Areas", "Private Residences", "Gallery Spaces", "Public Plazas", "Retail Entrances", "Restaurant Interiors", "Outdoor Terraces"],
    materials: ["Bronze", "Brass", "Corten Steel", "Mirror Stainless", "Patinated Copper"],
    specifications: {
      finishes: "Brushed, Mirror, Patina, Powder Coat",
      editions: "Limited runs of 10-50 pieces",
      customization: "Size, finish, and material variations available",
      leadTime: "8-12 weeks"
    }
  }
];

// Services data - kept for backward compatibility but renamed conceptually
export const services = [
  {
    id: 1,
    title: "Architectural Installations",
    subtitle: "Facades • Screens • Cladding",
    description: "Large-scale metal expressions that define buildings. We partner with architects to create landmark facade systems and architectural metal installations.",
    get image() { return getAIImage('studio_architectural'); },
    features: [
      "Parametric Facades",
      "Perforated Systems",
      "Contemporary Mashrabiya",
      "Metal Cladding",
      "Feature Exteriors",
      "Engineering Documentation"
    ]
  },
  {
    id: 2,
    title: "Interior Expressions",
    subtitle: "Feature Walls • Sculptural Elements • Ceilings",
    description: "Transforming interior spaces through metal artistry. From sculptural staircases to backlit feature walls that elevate spaces into experiential design.",
    get image() { return getAIImage('studio_interiors'); },
    features: [
      "Feature Walls",
      "Sculptural Staircases",
      "Metal Ceilings",
      "Balustrades",
      "Backlit Elements",
      "Integrated Lighting"
    ]
  },
  {
    id: 3,
    title: "The Art Atelier",
    subtitle: "Sculptures • Artist Collaborations • Public Art",
    description: "Our Art Atelier brings together master craftsmen and contemporary artists to create sculptures and installations that push boundaries.",
    get image() { return getAIImage('studio_art'); },
    features: [
      "Monumental Sculptures",
      "Kinetic Art",
      "Public Installations",
      "Artist Collaborations",
      "Gallery Works",
      "Prototyping Services"
    ]
  },
  {
    id: 4,
    title: "Retail & Brand",
    subtitle: "Storefronts • VM Displays • Brand Environments",
    description: "Crafting the physical expression of brands. Custom metalwork that embodies brand values and creates lasting impressions.",
    get image() { return getAIImage('studio_retail'); },
    features: [
      "Flagship Stores",
      "Visual Merchandising",
      "Brand Signage",
      "Pop-up Installations",
      "Exhibition Stands",
      "Global Rollout"
    ]
  }
];

export const capabilities = [
  {
    id: 1,
    slug: "architectural-metal-facades",
    title: "Architectural Metal Facades",
    subtitle: "Large-Scale Architectural Expression",
    description: "We create building envelopes that are more than weather barriers—they're the architectural statement itself.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    expertise: ["Rainscreen Systems", "Parametric Panels", "Unitized Facades", "Curtain Wall Integration"],
    materials: ["Aluminum", "Stainless Steel", "Copper", "Zinc", "Corten"],
    stats: { projects: 120, maxSize: "50,000 sqm", regions: "GCC-wide" }
  },
  {
    id: 2,
    slug: "perforated-decorative-screens",
    title: "Perforated & Decorative Screens",
    subtitle: "Light, Shadow, and Pattern",
    description: "Screens that create atmosphere through the interplay of solid and void, light and shadow.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    expertise: ["CNC Perforation", "Laser Cutting", "Mashrabiya Patterns", "Privacy Screens"],
    materials: ["Aluminum", "Steel", "Brass", "Bronze"],
    stats: { patterns: "500+", openArea: "15-60%", maxPanel: "3000x1500mm" }
  },
  {
    id: 3,
    slug: "cnc-laser-cutting",
    title: "CNC & Laser Cutting",
    subtitle: "Precision at Any Scale",
    description: "State-of-the-art cutting technology that translates complex designs into precise metal reality.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    expertise: ["Fiber Laser", "CO2 Laser", "Waterjet", "Plasma Cutting"],
    materials: ["All Metals", "Composites", "Acrylics"],
    stats: { accuracy: "±0.1mm", maxThickness: "25mm steel", bedSize: "3000x6000mm" }
  },
  {
    id: 4,
    slug: "special-metal-finishes",
    title: "Special Metal Finishes",
    subtitle: "The Surface is the Experience",
    description: "Museum-grade surface treatments that transform metal into tactile, visual experiences.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    expertise: ["PVD Coating", "Electroplating", "Patination", "Custom Textures"],
    materials: ["Stainless Steel", "Brass", "Bronze", "Copper"],
    stats: { finishes: "80+", colors: "Unlimited", durability: "20+ years" }
  },
  {
    id: 5,
    slug: "interior-metal-elements",
    title: "Interior Metal Elements",
    subtitle: "Crafting Interior Experiences",
    description: "Bespoke interior metalwork that elevates spaces through material and craft.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    expertise: ["Feature Walls", "Staircases", "Ceilings", "Millwork Integration"],
    materials: ["Bronze", "Brass", "PVD Steel", "Mixed Media"],
    stats: { projects: 200, sectors: "Hospitality, Residential, Corporate" }
  },
  {
    id: 6,
    slug: "balustrades-railings",
    title: "Balustrades & Railings",
    subtitle: "Safety Meets Sculpture",
    description: "Balustrade systems that satisfy code requirements while creating visual impact.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    expertise: ["Glass Integration", "Cable Systems", "Solid Panels", "Code Compliance"],
    materials: ["Stainless Steel", "Bronze", "Powder Coated Steel"],
    stats: { compliance: "UAE/BS/EN", finishes: "40+" }
  },
  {
    id: 7,
    slug: "lighting-integration",
    title: "Lighting Integration",
    subtitle: "Metal + Light in Harmony",
    description: "Collaborative metal and lighting solutions developed with our partner Ledworld.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    expertise: ["Backlit Panels", "LED Channels", "Sculptural Lighting", "Facade Illumination"],
    partnership: "Ledworld",
    stats: { projects: 50, types: "Interior & Exterior" }
  },
  {
    id: 8,
    slug: "signage-wayfinding",
    title: "Signage & Wayfinding",
    subtitle: "Architectural Identity Systems",
    description: "Signage that becomes architecture—integrated, crafted, and built to last.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    expertise: ["Monument Signs", "Wayfinding Systems", "Illuminated Signage", "Brand Integration"],
    materials: ["Stainless Steel", "Bronze", "Aluminum", "Mixed Media"],
    stats: { projects: 80, maxSize: "Unlimited" }
  },
  {
    id: 9,
    slug: "acrylic-translucent-elements",
    title: "Translucent Architectural Elements",
    subtitle: "Light Through Material",
    description: "Laser-cut acrylics and translucent materials that bring luminosity to architectural design.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/studio_translucent.webp",
    expertise: ["Laser Cut Acrylic", "Backlit Panels", "Decorative Screens", "Light Diffusion"],
    materials: ["Acrylic", "Polycarbonate", "Resin Panels"],
    stats: { maxThickness: "30mm", colors: "Full spectrum" }
  },
  {
    id: 10,
    slug: "artist-collaborations",
    title: "Artist Collaborations",
    subtitle: "The Art Atelier Program",
    description: "Our program for collaborating with artists on sculptures, installations, and public art commissions.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/studio_artist_collab.webp",
    expertise: ["Sculpture Fabrication", "Kinetic Art", "Public Art", "Gallery Installations"],
    materials: ["Corten", "Bronze", "Stainless", "Mixed Media"],
    stats: { collaborations: 35, publicArt: 12 }
  }
];

// Case studies / Portfolio projects
export const caseStudies = [
  {
    id: "khawaneej-villa-facade",
    title: "Khawaneej Villa",
    location: "Khawaneej, Dubai",
    category: "Residential",
    year: 2024,
    client: "Private Client",
    story: "A luxurious contemporary villa featuring bespoke metal facade elements that blend modern design with traditional Arabian aesthetics.",
    challenge: "Creating perforated metal screens that provide privacy and shade while maintaining an elegant, light appearance.",
    solution: "Custom bronze-finished mashrabiya panels with geometric patterns, precision-cut for seamless integration with the villa's architecture.",
    result: "A stunning residential facade that has become a landmark in the Khawaneej community.",
    materials: ["Bronze", "Perforated Aluminum", "Custom Finish"],
    tags: ["Residential", "Facade", "Villa", "Mashrabiya"],
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    gallery: []
  },
  {
    id: "grand-hyatt-abudhabi-facade",
    title: "Grand Hyatt Abu Dhabi",
    location: "Abu Dhabi, UAE",
    category: "Hospitality",
    year: 2023,
    client: "Hyatt Hotels Corporation",
    story: "Delivering premium metal cladding for one of Abu Dhabi's most prestigious hospitality developments.",
    challenge: "Creating a distinctive facade that reflects the hotel's luxury positioning while meeting strict technical requirements.",
    solution: "Bronze-finished aluminum panels with contemporary geometric patterns, engineered for the UAE's harsh climate.",
    result: "A signature hotel facade that sets new standards for hospitality architecture in the region.",
    materials: ["Aluminum", "Bronze Finish", "Glass Integration"],
    tags: ["Hospitality", "Facade", "Hotel", "Luxury"],
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    gallery: []
  },
  {
    id: "mall-jumeirah-entrance",
    title: "Mall of Jumeirah",
    location: "Jumeirah, Dubai",
    category: "Commercial",
    year: 2024,
    client: "Meraas",
    story: "Creating a grand entrance experience for Dubai's newest luxury retail destination with spectacular metal canopy structures.",
    challenge: "Engineering a sweeping metal canopy with integrated lighting that spans the mall's main entrance.",
    solution: "Stainless steel structural framework with perforated ceiling panels, featuring dynamic LED integration.",
    result: "An iconic entrance that has become a must-visit destination for visitors and shoppers alike.",
    materials: ["Stainless Steel", "Perforated Panels", "LED Integration"],
    tags: ["Commercial", "Retail", "Mall", "Canopy"],
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    gallery: []
  },
  {
    id: "damas-jewellery-signage",
    title: "Damas Jewellery Signage",
    location: "Dubai Mall, UAE",
    category: "Signage & Retail",
    year: 2024,
    client: "Damas International",
    story: "Crafting premium illuminated signage and facade elements for one of the Middle East's most renowned jewelry brands.",
    challenge: "Creating signage that reflects Damas's heritage of luxury while integrating seamlessly with modern retail architecture.",
    solution: "Gold and bronze channel letters with brushed metal facade cladding, complemented by sophisticated display integration.",
    result: "A flagship retail presence that elevates the brand experience for Damas customers.",
    materials: ["Bronze", "Gold Finish", "LED Illumination"],
    tags: ["Signage", "Retail", "Luxury", "Branding"],
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    gallery: []
  },
  {
    id: "cartier-flagship",
    title: "Cartier Flagship Store",
    location: "Dubai Mall, UAE",
    category: "Retail & Brand",
    year: 2023,
    client: "Cartier Middle East",
    story: "Crafting the metal elements for Cartier's regional flagship - where Parisian luxury meets Dubai opulence.",
    challenge: "Matching Cartier's exacting global standards while working within tight retail construction timelines.",
    solution: "Pre-fabricated modular elements with factory-applied finishes, reducing on-site installation to days.",
    result: "A flagship store that expresses the Cartier brand through impeccable metalcraft.",
    materials: ["PVD Rose Gold", "Mirror Stainless", "Brass Accents"],
    tags: ["Luxury Retail", "Global Brand", "Premium Finish"],
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    gallery: []
  },
  {
    id: "alserkal-installation",
    title: "Alserkal Gallery Installation",
    location: "Al Quoz, Dubai",
    category: "Art Installation",
    year: 2024,
    client: "Gallery Isabelle van den Eynde",
    story: "A commissioned art installation exploring the relationship between industrial process and artistic expression.",
    challenge: "Creating an installation that celebrates the raw beauty of metalworking while maintaining gallery-appropriate finishes.",
    solution: "Controlled oxidation processes, preserved weld marks, and raw material edges alongside precision-finished surfaces.",
    result: "An artwork that sparked conversations about craft, industry, and art.",
    materials: ["Raw Steel", "Controlled Rust", "Mirror Sections"],
    tags: ["Gallery", "Contemporary Art", "Material Exploration"],
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    gallery: []
  }
];

// Finishes library
export const finishes = [
  {
    id: 1,
    name: "PVD Champagne Gold",
    code: "PVD-CG-001",
    category: "PVD Coating",
    description: "Warm champagne gold with subtle rose undertones. Our most specified finish for luxury interiors.",
    suitability: ["Interior", "Light Exterior"],
    durability: "20+ years interior",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    substrates: ["Stainless Steel", "Brass"]
  },
  {
    id: 2,
    name: "PVD Rose Gold",
    code: "PVD-RG-001",
    category: "PVD Coating",
    description: "Romantic rose gold with excellent consistency across large panels.",
    suitability: ["Interior"],
    durability: "20+ years interior",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    substrates: ["Stainless Steel"]
  },
  {
    id: 3,
    name: "Antique Bronze Patina",
    code: "PAT-AB-001",
    category: "Patina",
    description: "Hand-applied bronze patina with natural variation. Each piece unique.",
    suitability: ["Interior", "Protected Exterior"],
    durability: "Living finish - evolves",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    substrates: ["Bronze", "Brass"]
  },
  {
    id: 4,
    name: "Mirror Polish",
    code: "MEC-MP-001",
    category: "Mechanical",
    description: "Maximum reflectivity mirror finish. Dramatic visual impact.",
    suitability: ["Interior"],
    durability: "Requires maintenance",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    substrates: ["Stainless Steel", "Brass", "Bronze"]
  },
  {
    id: 5,
    name: "Brushed Satin",
    code: "MEC-BS-001",
    category: "Mechanical",
    description: "Directional brushed finish. Hides fingerprints, classic aesthetic.",
    suitability: ["Interior", "Exterior"],
    durability: "Excellent",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    substrates: ["Stainless Steel", "Aluminum", "Bronze"]
  },
  {
    id: 6,
    name: "Corten Weathered",
    code: "PAT-CW-001",
    category: "Patina",
    description: "Accelerated weathering for consistent rust patina. Protective oxide layer.",
    suitability: ["Exterior"],
    durability: "100+ years structural",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp",
    substrates: ["Corten Steel"]
  }
];

// Studio ethos / philosophy
export const studioEthos = {
  headline: "We are makers who think like designers",
  subline: "IHGIND Studio exists at the intersection of craft, engineering, and artistic vision.",
  values: [
    {
      title: "Craft First",
      description: "We believe in the irreplaceable value of skilled hands and deep material knowledge."
    },
    {
      title: "Design Partnership",
      description: "We collaborate as equals with architects and designers, contributing our expertise to realize shared visions."
    },
    {
      title: "Material Expression",
      description: "Every metal has a voice. We help it speak through appropriate process, finish, and form."
    },
    {
      title: "Responsible Making",
      description: "We are stewards of resources, committed to sustainable practices and local production."
    }
  ]
};

// Stats for the homepage
export const studioStats = [
  { value: "17", label: "Years of Craft" },
  { value: "500+", label: "Projects Realized" },
  { value: "33,000", label: "sqm Atelier Space" },
  { value: "120+", label: "Artisan Team" }
];

// Industries served (renamed to "Who We Collaborate With")
export const collaborators = [
  {
    slug: "architects",
    title: "Architects",
    description: "We extend your design vision through technical expertise and material knowledge.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp"
  },
  {
    slug: "interior-designers",
    title: "Interior Designers",
    description: "Bespoke metal elements that elevate your interior concepts.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp"
  },
  {
    slug: "artists",
    title: "Artists & Sculptors",
    description: "Technical partnership to realize your artistic visions in metal.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp"
  },
  {
    slug: "brands",
    title: "Luxury Brands",
    description: "Physical brand experiences crafted in metal.",
    image: "https://files.ihgbrands.com/ihg-ind/generated/capability_facades.webp"
  }
];


// Projects (alias for case studies for backward compatibility)
export const projects = caseStudies;

// Process Steps for the Process Page
export const processSteps = [
  {
    number: "01",
    title: "Concept Dialogue",
    description: "Understanding your vision, design intent, and project requirements through collaborative discussion.",
    details: [
      "Initial consultation and brief review",
      "Site assessment and constraints analysis",
      "Design intent clarification",
      "Preliminary budget discussion"
    ]
  },
  {
    number: "02",
    title: "Design Development",
    description: "Our team contributes material expertise and fabrication knowledge to refine and develop the concept.",
    details: [
      "Material selection guidance",
      "Finish recommendations",
      "Technical feasibility study",
      "Value engineering options"
    ]
  },
  {
    number: "03",
    title: "Engineering & Documentation",
    description: "Comprehensive technical documentation ensuring precision execution.",
    details: [
      "Shop drawings and details",
      "Structural calculations",
      "Connection details",
      "Compliance documentation"
    ]
  },
  {
    number: "04",
    title: "Sampling & Approval",
    description: "Physical samples and prototypes for approval before full production.",
    details: [
      "Material samples",
      "Finish samples",
      "Mock-up fabrication",
      "Client approval process"
    ]
  },
  {
    number: "05",
    title: "Fabrication",
    description: "Precision manufacturing in our 33,000 sqm atelier with artisan oversight.",
    details: [
      "CNC cutting and forming",
      "Welding and assembly",
      "Surface finishing",
      "Quality control inspection"
    ]
  },
  {
    number: "06",
    title: "Finishing",
    description: "Museum-grade surface treatments applied in controlled environments.",
    details: [
      "PVD coating",
      "Electroplating",
      "Powder coating",
      "Patination and texturing"
    ]
  },
  {
    number: "07",
    title: "Quality Assurance",
    description: "Rigorous testing and inspection before delivery.",
    details: [
      "Dimensional verification",
      "Finish inspection",
      "Packaging preparation",
      "Documentation compilation"
    ]
  },
  {
    number: "08",
    title: "Installation",
    description: "Professional installation with white-glove care and project documentation.",
    details: [
      "Site coordination",
      "Installation execution",
      "Final inspection",
      "Project handover"
    ]
  }
];

